
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  PieChart, Pie, Cell, Area, AreaChart
} from 'recharts';
import { ChartPie, ChartBar, ChartLine } from 'lucide-react';

// Mock data for charts
const allVisitsByMonth = [
  { name: 'Jan', indoor: 4, outdoor: 2, combined: 0 },
  { name: 'Feb', indoor: 3, outdoor: 1, combined: 2 },
  { name: 'Mar', indoor: 5, outdoor: 3, combined: 1 },
  { name: 'Apr', indoor: 7, outdoor: 4, combined: 2 },
  { name: 'May', indoor: 6, outdoor: 3, combined: 3 },
  { name: 'Jun', indoor: 8, outdoor: 5, combined: 4 },
  { name: 'Jul', indoor: 10, outdoor: 6, combined: 3 },
  { name: 'Aug', indoor: 9, outdoor: 5, combined: 4 },
  { name: 'Sep', indoor: 7, outdoor: 4, combined: 2 },
  { name: 'Oct', indoor: 8, outdoor: 3, combined: 5 },
  { name: 'Nov', indoor: 9, outdoor: 4, combined: 3 },
  { name: 'Dec', indoor: 11, outdoor: 6, combined: 4 }
];

const serviceTypeData = [
  { name: 'Indoor', value: 45 },
  { name: 'Outdoor', value: 30 },
  { name: 'Combined', value: 25 },
];

const allPlantCountData = [
  { date: '2025-01-05', count: 12 },
  { date: '2025-01-19', count: 14 },
  { date: '2025-02-02', count: 13 },
  { date: '2025-02-16', count: 15 },
  { date: '2025-03-02', count: 17 },
  { date: '2025-03-16', count: 18 },
  { date: '2025-03-30', count: 20 },
  { date: '2025-04-13', count: 22 },
  { date: '2025-04-27', count: 24 },
  { date: '2025-05-11', count: 26 },
  { date: '2025-05-25', count: 28 },
  { date: '2025-06-08', count: 30 },
  { date: '2025-06-22', count: 32 },
  { date: '2025-07-06', count: 34 },
  { date: '2025-07-20', count: 36 },
];

const recentServices = [
  { date: '2025-04-27', type: 'Indoor', plants: 24, techs: 'John S.', rating: 4.8 },
  { date: '2025-04-13', type: 'Combined', plants: 22, techs: 'Maria L.', rating: 4.9 },
  { date: '2025-03-30', type: 'Outdoor', plants: 20, techs: 'John S.', rating: 5.0 },
  { date: '2025-03-16', type: 'Indoor', plants: 18, techs: 'Alex D.', rating: 4.7 }
];

const COLORS = ['#9b87f5', '#6E59A5', '#D6BCFA'];

interface AnalyticsDashboardProps {
  dateRange?: {
    from: Date;
    to: Date;
  };
  viewType?: 'chart' | 'pie' | 'line';
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ 
  dateRange = { from: new Date(2025, 0, 1), to: new Date() },
  viewType = 'chart'
}) => {
  // Filter data based on date range
  const visitsByMonth = useMemo(() => {
    const startMonth = dateRange.from.getMonth();
    const endMonth = dateRange.to.getMonth();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    return allVisitsByMonth.filter((_, index) => {
      // If end month is less than start month, it means we're crossing a year boundary
      if (endMonth < startMonth) {
        return index >= startMonth || index <= endMonth;
      }
      return index >= startMonth && index <= endMonth;
    });
  }, [dateRange]);

  const plantCountData = useMemo(() => {
    return allPlantCountData.filter(item => {
      const date = new Date(item.date);
      return date >= dateRange.from && date <= dateRange.to;
    });
  }, [dateRange]);

  const renderVisitsByMonthChart = () => {
    switch (viewType) {
      case 'pie':
        // For pie chart, we need to transform the data
        const totalByType = visitsByMonth.reduce((acc, curr) => {
          acc.indoor += curr.indoor;
          acc.outdoor += curr.outdoor;
          acc.combined += curr.combined;
          return acc;
        }, { indoor: 0, outdoor: 0, combined: 0 });
        
        const pieData = [
          { name: 'Indoor', value: totalByType.indoor },
          { name: 'Outdoor', value: totalByType.outdoor },
          { name: 'Combined', value: totalByType.combined }
        ];
        
        return (
          <ChartContainer className="h-64" config={{ indoor: { color: "#9b87f5" }, outdoor: { color: "#6E59A5" }, combined: { color: "#D6BCFA" } }}>
            <PieChart>
              <Pie 
                data={pieData} 
                cx="50%" 
                cy="50%" 
                labelLine={false}
                outerRadius={80} 
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltipContent />} />
            </PieChart>
          </ChartContainer>
        );
        
      case 'line':
        return (
          <ChartContainer className="h-64" config={{ indoor: { color: "#9b87f5" }, outdoor: { color: "#6E59A5" }, combined: { color: "#D6BCFA" } }}>
            <LineChart data={visitsByMonth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip content={<ChartTooltipContent />} />
              <Legend />
              <Line type="monotone" dataKey="indoor" name="Indoor" stroke="#9b87f5" strokeWidth={2} activeDot={{ r: 8 }} />
              <Line type="monotone" dataKey="outdoor" name="Outdoor" stroke="#6E59A5" strokeWidth={2} activeDot={{ r: 8 }} />
              <Line type="monotone" dataKey="combined" name="Combined" stroke="#D6BCFA" strokeWidth={2} activeDot={{ r: 8 }} />
            </LineChart>
          </ChartContainer>
        );
        
      default: // 'chart' - bar chart
        return (
          <ChartContainer className="h-64" config={{ indoor: { color: "#9b87f5" }, outdoor: { color: "#6E59A5" }, combined: { color: "#D6BCFA" } }}>
            <BarChart data={visitsByMonth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip content={<ChartTooltipContent />} />
              <Legend />
              <Bar dataKey="indoor" name="Indoor" fill="#9b87f5" radius={[4, 4, 0, 0]} />
              <Bar dataKey="outdoor" name="Outdoor" fill="#6E59A5" radius={[4, 4, 0, 0]} />
              <Bar dataKey="combined" name="Combined" fill="#D6BCFA" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
        );
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <ChartBar className="h-5 w-5 text-primary" />
              Monthly Visits
            </CardTitle>
            <CardDescription>
              Service visits by month and type
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {renderVisitsByMonthChart()}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <ChartPie className="h-5 w-5 text-primary" />
              Service Types
            </CardTitle>
            <CardDescription>
              Distribution of service types
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <ChartContainer className="h-64" config={{ indoor: { color: "#9b87f5" }, outdoor: { color: "#6E59A5" }, combined: { color: "#D6BCFA" } }}>
              <PieChart>
                <Pie 
                  data={serviceTypeData} 
                  cx="50%" 
                  cy="50%" 
                  labelLine={false}
                  outerRadius={80} 
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {serviceTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltipContent />} />
              </PieChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <ChartLine className="h-5 w-5 text-primary" />
              Plant Growth
            </CardTitle>
            <CardDescription>
              Average plant count over time
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <ChartContainer className="h-64" config={{ count: { color: "#9b87f5" } }}>
              <AreaChart data={plantCountData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(value) => value.split('-')[1] + '/' + value.split('-')[2]} />
                <YAxis allowDecimals={false} />
                <Tooltip content={<ChartTooltipContent />} />
                <Area 
                  type="monotone" 
                  dataKey="count" 
                  name="Plant Count"
                  stroke="#9b87f5" 
                  fill="#9b87f5"
                  fillOpacity={0.2}
                  strokeWidth={2} 
                />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Services</CardTitle>
          <CardDescription>
            Your most recent plant care visits
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Service Type</TableHead>
                <TableHead>Plants</TableHead>
                <TableHead>Technician</TableHead>
                <TableHead>Rating</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentServices.map((service, index) => (
                <TableRow key={index}>
                  <TableCell>{service.date}</TableCell>
                  <TableCell>{service.type}</TableCell>
                  <TableCell>{service.plants}</TableCell>
                  <TableCell>{service.techs}</TableCell>
                  <TableCell>
                    <span className="flex items-center">
                      {service.rating}
                      <span className="ml-1 text-yellow-500">★</span>
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsDashboard;
